/**
 * The last thing standing when a screen throws.
 *
 * React unmounts the whole tree on an uncaught render error, so without a
 * boundary one bad value anywhere takes the entire product with it: no header,
 * no navigation, no way back — a white page, and an owner who has no idea
 * whether their data is gone.
 *
 * That is not hypothetical. The Riders screen rendered a rate the API sent as
 * "500.0000"; the money parser refused it, correctly, because silently rounding
 * money is the thing it exists to prevent; and app.cenox.ai went blank.
 *
 * So a boundary goes around each screen, not around the app. Around the app it
 * would still cost the person their navigation. Around a screen, the one that
 * broke says so and the other six keep working — which is also the honest
 * report: something here is wrong, the rest is fine.
 *
 * It deliberately does not retry on its own. A render that threw once throws
 * again on the same data, and a boundary that loops is harder to diagnose than
 * one that stops.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  /** Named in the message, so the person can say which screen failed. */
  screen: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ScreenError extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is where this gets diagnosed; keep the component stack with it.
    console.error(`[${this.props.screen}] render failed`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900"
      >
        <p className="font-medium">This screen could not be displayed.</p>
        <p className="mt-1.5 text-amber-800">
          Something in the {this.props.screen} data is not in a shape this page can read. Nothing
          has been changed — the other Logistics screens still work, and reloading is safe.
        </p>
        {/*
          The message, not the stack. "Invalid money value \"500.0000\"" is the
          one line that tells whoever is helping where to look, and hiding it
          only means it has to be fished out of a console over the phone.
        */}
        <p className="mt-3 break-words font-mono text-xs text-amber-700">{error.message}</p>
      </div>
    );
  }
}
